import { AxiosResponse } from 'axios';
import { baseClient } from '@/core/infrastructure/api/apiClient';
import { translate } from '@/core/utils/translate';
import {
  ApiResponse,
  BulkDeletePayload,
  PaginatedRoleResponse,
  PermissionAddPayload,
  PermissionNode,
  PermissionUpdatePayload,
  RoleCreateData,
  RoleCreatePayload,
  RoleItem,
  RoleListParams,
  RoleUpdateData,
  RoleUpdatePayload,
} from './type';

export const rolesQueries = {
  keys: {
    all: ['roles'] as const,
    list: (params?: Partial<RoleListParams>) => [...rolesQueries.keys.all, 'list', params] as const,
    detail: (id: string) => [...rolesQueries.keys.all, 'detail', id] as const,
    permissions: () => [...rolesQueries.keys.all, 'permissions'] as const,
    rolePermissions: (roleId: string) =>
      [...rolesQueries.keys.all, 'rolePermissions', roleId] as const,
    userPermissions: () => [...rolesQueries.keys.all, 'userPermissions'] as const,
  },

  fn: {
    // Fetch roles list with pagination and filters
    fetchRolesList: async (params: RoleListParams): Promise<PaginatedRoleResponse> => {
      try {
        const payload = {
          index: params.index,
          size: params.size,
          search: params.search || '',
          platformTypes: params.platformTypes || [],
          departmentIds: params.departmentIds || [],
        };

        const response = await baseClient.post<ApiResponse<PaginatedRoleResponse>>(
          'identity/roles/search',
          payload
        );
        return response.data.body;
      } catch (error: any) {
        throw new Error(
          translate('errors/api:roles_list_fetch_failed', {
            error: error.response?.data?.message || error.message,
          })
        );
      }
    },

    // Fetch role by ID
    fetchRoleById: async (roleId: string): Promise<RoleItem> => {
      try {
        const response = await baseClient.get<ApiResponse<RoleItem>>(`identity/roles/${roleId}`);
        return response.data.body;
      } catch (error: any) {
        throw new Error(
          translate('errors/api:role_fetch_failed', {
            error: error.response?.data?.message || error.message,
          })
        );
      }
    },

    // Fetch permissions for a specific role
    fetchPermissionsByRoleId: async (roleId: string): Promise<string[]> => {
      try {
        const response = await baseClient.get<ApiResponse<string[]>>(
          `identity/roles/${roleId}/permissions`
        );
        return response.data.body;
      } catch (error: any) {
        throw new Error(
          translate('errors/api:role_permissions_fetch_failed', {
            error: error.response?.data?.message || error.message,
          })
        );
      }
    },

    // Fetch all available permissions
    fetchUserPermissions: async (): Promise<string[]> => {
      try {
        const response = await baseClient.get<ApiResponse<string[]>>('identity/roles/permissions', {
          headers: {
            accept: 'application/json',
          },
        });
        return response.data.body;
      } catch (error: any) {
        throw new Error(
          translate('errors/api:permissions_fetch_failed', {
            error: error.response?.data?.message || error.message,
          })
        );
      }
    },

    // Fetch permissions by IDs
    fetchAllPermissions: async (): Promise<PermissionNode[]> => {
      try {
        const searchPayload = {
          ids: [],
          isHide: true,
        };
        const response = await baseClient.post<ApiResponse<PermissionNode[]>>(
          'permissions/search',
          searchPayload
        );
        return response.data.body;
      } catch (error: any) {
        throw new Error(
          translate('errors/api:permissions_fetch_failed', {
            error: error.response?.data?.message || error.message,
          })
        );
      }
    },
  },

  mutations: {
    // Create new role
    createRole: async (data: RoleCreateData): Promise<AxiosResponse<any, any>> => {
      const payload: RoleCreatePayload = {
        name: data.name,
        description: data.roleDescription,
        platformType: data.platformType,
        departmentId: data.department,
      };

      const response = await baseClient.post('identity/roles', payload);
      return response;
    },

    // Update existing role
    updateRole: async ({
      roleId,
      data,
    }: {
      roleId: string;
      data: RoleUpdateData;
    }): Promise<AxiosResponse<any, any>> => {
      const payload: RoleUpdatePayload = {
        name: data.name,
        description: data.description,
        platformType: data.platformType,
        departmentId: data.departmentId,
      };

      const response = await baseClient.put(`identity/roles/${roleId}`, payload);
      return response;
    },

    // Delete roles in bulk
    deleteRoles: async (ids: string[]): Promise<AxiosResponse<any, any>> => {
      const payload: BulkDeletePayload = { ids };
      const response = await baseClient.delete('identity/roles/bulk', {
        data: payload,
      });
      return response;
    },

    // Add permissions to role
    addPermissionsToRole: async (
      payload: PermissionAddPayload
    ): Promise<AxiosResponse<any, any>> => {
      const response = await baseClient.post('identity/roles/permission', payload);
      return response;
    },

    // Update role permissions
    updateRolePermissions: async (
      payload: PermissionUpdatePayload
    ): Promise<AxiosResponse<any, any>> => {
      const response = await baseClient.post('identity/roles/permission', payload);
      return response;
    },
  },
};
